import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2, PenLine } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { mintSigningTokenFor } from '@/lib/signingToken'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import type { SignerOption } from '@/api/signingApi'
import {
  initialSignerId,
  signerBlocker,
  signerChoices,
  signerName as chosenSignerName,
} from '@/lib/signerChoices'
import { SignerPicker } from '@/components/signature/SignerPicker'
import { useOptionalAuth } from '@/providers/AuthProvider'

// Shared electronic-signature dialog (21 CFR Part 11). The signer re-enters
// their own password and the dialog exchanges it for a single-use signing token
// (`lib/signingToken.ts`), which the caller sends with the action it is signing.
//
// The signer's IDENTITY is never typed: it is the authenticated principal, or
// — with `signAs` — a person picked from the tenant roster who then types THEIR
// OWN password here ("Sign as", z8nrz7dt3z). Either way the server records the
// person whose password minted the token. Used by the renderer's signature
// fields and every version-control event: save-as-new-version,
// update-template-to-latest-form, activate/inactivate.

/** "Sign as" support for a dialog. Absent = the signed-in user signs. */
export interface SignAsConfig {
  /** `optional` defaults to the signed-in user; `required` makes the operator pick. */
  mode: 'optional' | 'required'
  /** The picker's label — "Sign as" or "Remove as", after the legacy menu. */
  label?: string
  /** The roster for this action; undefined while it loads. */
  options: SignerOption[] | undefined
  loading?: boolean
  error?: string | null
  /** The signed-in user's id — picking them is an ordinary signature. */
  selfId: string | null
}

/** Who a confirmed signature is for. `id` is null for the signed-in user. */
export interface ConfirmedSigner {
  id: string | null
  name: string
}

export interface SignatureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  /** Extra content rendered above the signature input (e.g. a version field). */
  children?: ReactNode
  confirmLabel?: string
  /** The signer, shown for confirmation — they cannot type a different one. */
  signerName?: string
  /** Disables confirm beyond the empty-name check (parent-side validation). */
  confirmDisabled?: boolean
  busy?: boolean
  error?: string | null
  /** Offer "Sign as" — picking a different signer. */
  signAs?: SignAsConfig
  /**
   * More single-use tokens from the same password entry, for one act that two
   * endpoints each record (a product label recorded, then printed on a
   * "sign to print" printer). Each token is still spent by its own endpoint.
   */
  extraTokens?: number
  /** Receives the single-use signing token to send with the signed action. */
  onConfirm: (signingToken: string, signer: ConfirmedSigner, extraTokens: string[]) => void
}

export function SignatureDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm & Sign',
  signerName = '',
  confirmDisabled = false,
  busy = false,
  error = null,
  signAs,
  extraTokens = 0,
  onConfirm,
}: SignatureDialogProps) {
  const [password, setPassword] = useState('')
  const [minting, setMinting] = useState(false)
  const [mintError, setMintError] = useState<string | null>(null)
  const mode = signAs?.mode ?? 'self'
  const selfId = signAs?.selfId ?? null
  const [chosenId, setChosenId] = useState<string | null>(initialSignerId(mode, selfId))
  const ssoOnly = useOptionalAuth()?.user?.ssoOnly === true

  // Clear the password every time the dialog opens or closes. A credential left
  // in state outlives the signature it was for, and the next signing event must
  // be its own deliberate act. The picked signer resets with it: the next
  // signature is not presumed to be the same colleague's.
  useEffect(() => {
    setPassword('')
    setMintError(null)
    setChosenId(initialSignerId(mode, selfId))
  }, [open, mode, selfId])

  const choices = useMemo(
    () => signerChoices(signAs?.options ?? [], selfId),
    [signAs?.options, selfId],
  )
  const blocker = signerBlocker(mode, chosenId, choices, selfId)
  const signingFor = chosenSignerName(chosenId, choices, selfId, signerName)
  const isOther = chosenId !== null && chosenId !== selfId
  // An SSO-only signer signs with their separate signing password.
  const signingPassword = ssoOnly && !isOther

  // A new signer means a different password is expected; never carry a typed
  // one across to them.
  const choose = (id: string) => {
    setChosenId(id)
    setPassword('')
    setMintError(null)
  }

  const sign = async () => {
    if (password === '' || minting || blocker) return
    setMinting(true)
    setMintError(null)
    try {
      const signerId = isOther ? chosenId : null
      const token = await mintSigningTokenFor(password, signerId, selfId)
      const extra: string[] = []
      for (let i = 0; i < extraTokens; i++) extra.push(await mintSigningTokenFor(password, signerId, selfId))
      onConfirm(token, { id: signerId, name: signingFor }, extra)
      setPassword('')
    } catch (err) {
      setMintError(apiErrorMessage(err, 'Password could not be verified.'))
    } finally {
      setMinting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !busy && onOpenChange(o)}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className='space-y-4 py-2'>
          {children}
          <div className='space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3'>
            <p className='text-xs font-semibold text-primary'>Electronic Signature (required)</p>
            {signAs && (
              <SignerPicker
                label={signAs.label ?? 'Sign as'}
                choices={choices}
                value={chosenId}
                onChange={choose}
                loading={signAs.loading}
                error={signAs.error}
                disabled={busy || minting}
              />
            )}
            {signAs && blocker && chosenId !== null && (
              <p className='text-xs text-amber-700'>{blocker}</p>
            )}
            {signingFor && (mode === 'self' || chosenId !== null) && (
              <p className='text-[11px] text-muted-foreground'>
                Signing as {signingFor}
                {isOther && ' — they must type their own password'}
              </p>
            )}
            <Label htmlFor='signature-password' className='text-xs'>
              {isOther ? `Password for ${signingFor}` : signingPassword ? 'Signing password' : 'Password'}
            </Label>
            {/* A DECOY, and it has to stay.
             *
             * Chrome pairs a password field with a "username" field by hunting
             * the page for the nearest text input — it does not stop at this
             * dialog. With nothing here to find it reached out and autofilled
             * an unrelated input on the page behind: opening this dialog over
             * the materials table put the signed-in user's email into that
             * table's SEARCH box, which is a controlled input, so the list
             * silently filtered itself down to nothing. Absorbing the pairing
             * here keeps the guess inside the dialog where it is harmless.
             *
             * aria-hidden + tabIndex -1 so it is inert to keyboard and to
             * screen readers; it must stay in the DOM (not `display:none`) or
             * the browser skips it and goes hunting again.
             *
             * It must also stay WRITABLE. A `readOnly` decoy is not a field the
             * browser can fill, so Chrome discards it as a candidate and
             * resumes the same hunt it was meant to end — which is exactly how
             * the search box kept getting the email even with this decoy in
             * place. Uncontrolled for the same reason: React reverting the
             * value React-side does not withdraw the field from the browser's
             * pairing, but a controlled `value=''` does make a filled decoy
             * look empty again, which invites a second attempt. Nothing ever
             * reads this input. */}
            <input
              type='text'
              name='username'
              autoComplete='username'
              aria-hidden='true'
              tabIndex={-1}
              className='pointer-events-none absolute h-0 w-0 opacity-0'
              defaultValue=''
            />
            {/* Deliberately hostile to password managers: a credential replayed
             * from a browser store was not executed BY the signer AT the moment
             * of signing, which is the whole point of re-entry. The renderer's
             * SignatureConfirmPanel carries the same guards for the same reason.
             * `new-password` rather than `off`: browsers have largely stopped
             * honouring `off` on password inputs, and still honour this. */}
            <Input
              id='signature-password'
              type='password'
              name='signature-password'
              autoComplete='new-password'
              data-1p-ignore
              data-lpignore='true'
              data-form-type='other'
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !confirmDisabled) void sign()
              }}
              placeholder={
                isOther
                  ? `${signingFor}'s own password…`
                  : signingPassword
                    ? 'Your signing password…'
                    : 'Your account password…'
              }
              autoFocus={!children && !signAs}
              disabled={mode === 'required' && chosenId === null}
            />
            {signingPassword && (
              // New tab, so the signature in progress keeps its state; setting it may need a fresh SSO sign-in.
              <a
                href='/profile#signing-password'
                target='_blank'
                rel='noreferrer'
                className='text-xs text-primary hover:underline'
              >
                Set or reset signing password
              </a>
            )}
          </div>
          {(mintError ?? error) && (
            <p className='text-sm text-destructive'>{mintError ?? error}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy || minting}>
            Cancel
          </Button>
          <Button
            onClick={() => void sign()}
            disabled={busy || minting || confirmDisabled || password === '' || !!blocker}
          >
            {busy || minting ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <PenLine className='mr-2 h-4 w-4' />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
